import { 
  StreamInfo, 
  StreamListResponse, 
  StreamMetadata, 
  StreamStats,
  CapabilitiesResponse,
  ValidationResponse,
  AvailableFormatsResponse
} from '../types';

const DEFAULT_HTTP_BASE = 'http://localhost:5122';

const normalizeBaseUrl = (baseUrl: string): string => {
  if (!baseUrl) {
    return DEFAULT_HTTP_BASE;
  }
  return baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
};

export const getDefaultHttpBase = (): string => {
  if (typeof window === 'undefined') {
    return DEFAULT_HTTP_BASE;
  }
  // Check if we are running under a subpath (e.g. /streaming/)
  const pathPrefix = window.location.pathname.includes('/streaming/') ? '/streaming' : '';
  return `${window.location.origin}${pathPrefix}`;
};

const buildUrl = (baseUrl: string, path: string): string => {
  const normalized = normalizeBaseUrl(baseUrl);
  return `${normalized}${path}`;
};

const handleResponse = async <T>(response: Response): Promise<T> => {
  if (!response.ok) {
    const text = await response.text();
    throw new Error(
      `Request failed (${response.status}): ${text || response.statusText || 'Unknown error'}`,
    );
  }
  return (await response.json()) as T;
};

export const fetchStreams = async (baseUrl: string): Promise<StreamInfo[]> => {
  const response = await fetch(buildUrl(baseUrl, '/api/debug/streams'), {
    headers: { Accept: 'application/json' },
  });

  const data = await handleResponse<StreamListResponse>(response);
  return data.streams ?? [];
};

export const fetchStreamStats = async (baseUrl: string, streamId: string): Promise<StreamStats> => {
  const response = await fetch(
    buildUrl(baseUrl, `/api/debug/stream/${encodeURIComponent(streamId)}/stats`),
    { headers: { Accept: 'application/json' } },
  );
  return handleResponse<StreamStats>(response);
};

export const fetchStreamMetadata = async (
  baseUrl: string,
  streamId: string,
): Promise<StreamMetadata> => {
  const response = await fetch(
    buildUrl(baseUrl, `/api/debug/stream/${encodeURIComponent(streamId)}/metadata`),
    { headers: { Accept: 'application/json' } },
  );
  const data = await handleResponse<{ metadata: StreamMetadata }>(response);
  return data.metadata ?? {};
};

// Capability Discovery APIs
export const fetchCapabilities = async (baseUrl: string): Promise<CapabilitiesResponse> => {
  const response = await fetch(buildUrl(baseUrl, '/api/capabilities/formats'), {
    headers: { Accept: 'application/json' },
  });
  return handleResponse<CapabilitiesResponse>(response);
};

export const validateConversion = async (
  baseUrl: string,
  sourceCodec: string,
  targetFormatId: string,
): Promise<ValidationResponse> => {
  const response = await fetch(
    buildUrl(baseUrl, `/api/capabilities/validate?sourceCodec=${encodeURIComponent(sourceCodec)}&targetFormatId=${encodeURIComponent(targetFormatId)}`),
    { headers: { Accept: 'application/json' } },
  );
  return handleResponse<ValidationResponse>(response);
};

export const fetchAvailableFormats = async (
  baseUrl: string,
  sourceCodec: string,
): Promise<AvailableFormatsResponse> => {
  const response = await fetch(
    buildUrl(baseUrl, `/api/capabilities/available?sourceCodec=${encodeURIComponent(sourceCodec)}`),
    { headers: { Accept: 'application/json' } },
  );
  return handleResponse<AvailableFormatsResponse>(response);
};

